import { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CodoidAPITesting = () => {
  const [currentSlide, setCurrentSlide] = useState(3);
  const totalSlides = 5;
  const [activeSection, setActiveSection] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const sliderRef = useRef(null);

  // Define color constants with hex codes - using violet palette
  const COLORS = {
    primary: "#6b2c91",      // Main violet
    secondary: "#f3e8ff",    // Light violet background
    accent: "#9333ea",       // Bright violet accent
    text: "#333333",         // Dark text
    lightText: "#666666",    // Light text
    background: "#f9fafb",   // Light background
    active: "#8b5cf6",       // Active state
    inactive: "#d1d5db",     // Inactive state
    cardBg: "#ffffff",       // Card background
    navBg: "#ede9fe",        // Navigation background
    headingBg: "#f0e6ff",    // Heading background
    headingUnderline: "#9333ea" // Heading underline
  };

  // Testing service names
  const testingServices = [
    { id: 1, name: "Automation Testing" },
    { id: 2, name: "Mobile App Testing" },
    { id: 3, name: "API Testing" },
    { id: 4, name: "Accessibility Testing" },
    { id: 5, name: "Data Analytics Testing" }
  ];

  // Tools with image property
  const apiTools = [
    { name: "SoapUI", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/SoapUI.webp?updatedAt=1747021968123" },
    { name: "REST Assured", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/REST-Assured.png?updatedAt=1747021967742" },
    { name: "SwaggerUI", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/Swagger-UI.png?updatedAt=1747021967448" },
    { name: "Postman", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/Postman.png?updatedAt=1747021967493" },
    { name: "Katalon", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/Katalon.png?updatedAt=1747021966421" },
    { name: "GraphQL", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/Grapj.png?updatedAt=1747021965317" },
    { name: "Apigee", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/Apigee.png?updatedAt=1747021964530" },
    { name: "Citrus", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/Citrus.png?updatedAt=1747021964316" },
    { name: "API Fortress", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/API-Fortress.png?updatedAt=1747021964292" },
    { name: "Apache JMeter", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/API%20Testing/Apache-J-meter.png?updatedAt=1747021964110" }
  ];

  const mobileTools = [
    { name: "Xamarin", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Xamarin.png?updatedAt=1747021951762" },
    { name: "Perfecto", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Perfecto.png?updatedAt=1747021948011" },
    { name: "Kobiton", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Kobiton.png?updatedAt=1747021948154" },
    { name: "Ranorex Studio", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Ranorex-Studio.webp?updatedAt=1747021948062" },
    { name: "Appium", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Appium.webp?updatedAt=1747021948160" }
  
    , { name: "Espresso", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Expresso.png?updatedAt=1747021948145" },
    { name: "Apptim", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Apptim.png?updatedAt=1747021948150" },
    { name: "Robotium", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Robotium.png?updatedAt=1747021948371" },
    
    { name: "BrowserStack", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Mobile%20App%20Testing/Browser-stack.webp?updatedAt=1747021948377" }
  
  ];

  const automationTools = [
     { name: "TestComplete", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Testcomplete.png?updatedAt=1747021964131" },
    { name: "Wave", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Specflow.webp?updatedAt=1747021964118" },
    { name: "Zephyr", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Zephyr.webp?updatedAt=1747021963936" },
    { name: "Selenium", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Selenium.png?updatedAt=1747021963220" },
    { name: "Kotlin", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Kotlin.webp?updatedAt=1747021962820" }
    , { name: "Jenkins", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/jenkins-1.webp?updatedAt=1747021961493" },
    { name: "Jira", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Jira-1.png?updatedAt=1747021960807" },
    { name: "Fla UI", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/FlaUI-1.webp?updatedAt=1747021960727" },
    { name: "Cypress", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Cypress-1.webp?updatedAt=1747021960648" },
    { name: "Cucumber", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Automation%20Testing/Cucumber-Logo.png?updatedAt=1747021960691" }
  
  ];

  const accessibilityTools = [
       { name: "Wave", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/Waves.webp?updatedAt=1747021972527" },
    { name: "VoiceOver", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/Voice-Over.webp?updatedAt=1747021972072" },
    { name: "CCA", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/TGPGI.png?updatedAt=1747021971198" },
    { name: "TalkBack", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/Talkback.webp?updatedAt=1747021970917" },
      { name: "ORCA", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/ORCA.webp?updatedAt=1747021969993" },
    { name: "NVDA", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/NVDA.webp?updatedAt=1747021970033" },
    { name: "JAWS", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/JAWS.png?updatedAt=1747021968952" },
    { name: "axe", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/Axe.png?updatedAt=1747021968619" },
      { name: "Chromevox", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/ChromeVox.webp?updatedAt=1747021968670" },
    { name: "ARC", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Accessibility%20Testing/ARC-Toolkit.webp?updatedAt=1747021968223" },
    
  ];

  const dataTools = [
    { name: "Splunk", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Data%20Analytics%20Testing/Splunk.png?updatedAt=1747021960131" },
    { name: "Snowflake", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Data%20Analytics%20Testing/Snowflake.png?updatedAt=1747021959961" },
    { name: "Looker", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Data%20Analytics%20Testing/Looker.png?updatedAt=1747021959823" },
    { name: "Lemon Cheesecake", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Data%20Analytics%20Testing/Lemon-cheesecake.webp?updatedAt=1747021958929" },
    { name: "DBT", image: "https://ik.imagekit.io/kqumni8l2/Testing%20page/Data%20Analytics%20Testing/DBT.png?updatedAt=1747021958727" }
  ];

  const allSlidesContent = [
    { id: 1, title: "Automation Testing", tools: automationTools },
    { id: 2, title: "Mobile App Testing", tools: mobileTools },
    { id: 3, title: "API Testing", tools: apiTools },
    { id: 4, title: "Accessibility Testing", tools: accessibilityTools },
    { id: 5, title: "Data Analytics Testing", tools: dataTools }
  ];

  const handlePrevSlide = () => {
    if (isTransitioning || currentSlide <= 1) return;
    changeSection(currentSlide - 1);
  };

  const handleNextSlide = () => {
    if (isTransitioning || currentSlide >= totalSlides) return;
    changeSection(currentSlide + 1);
  };

  const changeSection = (sectionNumber) => {
    if (isTransitioning || sectionNumber === activeSection) return;
    setIsTransitioning(true);
    setActiveSection(sectionNumber);
    setCurrentSlide(sectionNumber);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: COLORS.background }}>
      <div className="pt-8 pb-8 text-center">
        <div className="relative inline-block">
          <h1 className="text-4xl md:text-5xl font-bold mb-2 relative z-10">
            <span style={{ color: COLORS.primary }} className="relative text-3xl">
              Tools We Use
              <span 
                className="absolute bottom-1 left-0 w-full h-3" 
                style={{ backgroundColor: COLORS.headingUnderline, opacity: 0.3 }}
              ></span>
            </span>
          </h1>
        </div>
        <div 
          className="mx-auto max-w-lg rounded-lg py-2 px-4 mt-2"
          style={{ backgroundColor: COLORS.headingBg }}
        >
          <p style={{ color:"black" }} className="text-xl text-center">
            Professional Technologies for Quality Assurance
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row max-w-7xl mx-auto">
        <div className="w-full md:w-1/4 p-6 md:p-10" style={{ backgroundColor: COLORS.secondary }}>
          <div className="flex flex-col">
            {testingServices.map((service) => (
              <div
                key={service.id}
                className="flex items-center mb-10 cursor-pointer transition-all duration-300 hover:translate-x-2"
                onClick={() => !isTransitioning && changeSection(service.id)}
              >
                <div
                  className="rounded-full w-12 h-12 border-2 flex items-center justify-center transition-colors duration-300"
                  style={{ 
                    borderColor: activeSection === service.id ? COLORS.primary : COLORS.inactive,
                    backgroundColor: activeSection === service.id ? COLORS.secondary : 'transparent'
                  }}
                >
                  <span
                    className="font-medium"
                    style={{ color: activeSection === service.id ? COLORS.primary : "black" }}
                  >
                    {String(service.id).padStart(2, '0')}
                  </span>
                </div>
                <span
                  className="ml-4 font-medium transition-colors duration-300"
                  style={{ color: activeSection === service.id ? COLORS.primary : "black" }}
                >
                  {service.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full md:w-3/4 p-6 md:p-10 bg-white overflow-hidden shadow-sm rounded-r-lg">
          <div ref={sliderRef} className="overflow-x-hidden w-full">
            <div
              className="flex"
              style={{
                width: `${totalSlides * 100}%`,
                transform: `translateX(-${(activeSection - 1) * (100 / totalSlides)}%)`,
                transition: "transform 300ms ease-out"
              }}
            >
              {allSlidesContent.map((slide) => (
                <div key={slide.id} className="flex-shrink-0" style={{ width: `${100 / totalSlides}%` }}>
                  <div className="px-2">
                    <h2 
                      className="text-3xl font-semibold mb-10 inline-block relative"
                      style={{ color: COLORS.primary }}
                    >
                      {slide.title}
                      <span 
                        className="absolute bottom-0 left-0 w-1/3 h-1 rounded-full" 
                        style={{ backgroundColor: COLORS.primary }}
                      ></span>
                    </h2>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
                      {slide.tools.map((tool, index) => (
                        <div
                          key={index}
                          className="rounded-lg shadow-md p-4 flex flex-col items-center justify-center cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                          style={{ 
                            backgroundColor: COLORS.cardBg, 
                            height: "120px",
                            borderTop: `3px solid ${COLORS.accent}`
                          }}
                        >
                          <img src={tool.image} alt={tool.name} className="w-10 h-10 mb-3" />
                          <h3 
                            className="font-medium text-sm text-center"
                            style={{ color: COLORS.text }}
                          >
                            {tool.name}
                          </h3>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div 
            className="flex justify-center items-center mt-10 rounded-lg p-2"
            style={{ backgroundColor: COLORS.navBg }}
          >
            <button
              onClick={handlePrevSlide}
              className="p-2 rounded-full transition-colors duration-300 hover:bg-white"
              style={{ 
                color: currentSlide > 1 ? COLORS.primary : COLORS.inactive,
                opacity: currentSlide > 1 ? 1 : 0.5
              }}
              disabled={isTransitioning || currentSlide <= 1}
            >
              <ChevronLeft size={24} />
            </button>
            <span 
              className="mx-6 font-medium"
              style={{ color: COLORS.primary }}
            >
              {currentSlide}/{totalSlides}
            </span>
            <button
              onClick={handleNextSlide}
              className="p-2 rounded-full transition-colors duration-300 hover:bg-white"
              style={{ 
                color: currentSlide < totalSlides ? COLORS.primary : COLORS.inactive,
                opacity: currentSlide < totalSlides ? 1 : 0.5
              }}
              disabled={isTransitioning || currentSlide >= totalSlides}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodoidAPITesting;